/**
 * 브라우저에서 난 오류를 서버(`/api/client-error`)로 신고한다. (#262)
 *
 * 서버는 이걸 받아 구글 챗으로 실시간 알린다. 신고 자체가 실패해도 조용히
 * 넘어간다 — 오류 신고가 또 다른 오류를 만들면 안 된다.
 *
 * 클라이언트 컴포넌트에서 쓴다. 서버 코드는 `#/lib/error-alert` 를 직접 부른다.
 */

export interface ClientErrorReport {
  /** 어디서 났는지. 알림 제목에 `web/<scope>` 로 붙는다. 예: `billing-card` */
  scope: string;
  message: string;
  stack?: string;
  /** Next 가 서버 오류에 붙이는 digest — 서버 로그와 대조할 때 쓴다 */
  digest?: string;
  code?: string;
  componentStack?: string;
  severity?: 'error' | 'warn';
  /**
   * 원인을 가르는 데 필요한 부수 정보. 알림 본문에 `이름: 값` 으로 붙는다.
   * 하이드레이션 불일치의 번역 판별 근거가 여기로 온다. (#262)
   */
  signals?: Record<string, string>;
}

/** 같은 오류를 이 탭에서 반복 신고하지 않기 위한 기억 */
const reported = new Set<string>();

export function reportClientError(report: ClientErrorReport): void {
  if (typeof window === 'undefined') return;

  try {
    const key = `${report.scope}|${report.message}`.slice(0, 300);
    if (reported.has(key)) return;
    if (reported.size > 50) reported.clear();
    reported.add(key);

    const body = JSON.stringify({
      ...report,
      message: String(report.message ?? '').slice(0, 2000),
      stack: report.stack?.slice(0, 8000),
      url: window.location.href
    });

    // sendBeacon 은 페이지가 곧 닫혀도(오류 직후 이탈이 흔하다) 전송을 보장한다.
    // 실패하면 keepalive fetch 로 한 번 더 시도한다.
    const sent =
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(
        '/api/client-error',
        new Blob([body], { type: 'application/json' })
      );

    if (!sent) {
      void fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {
        /* 신고 실패는 무시 */
      });
    }
  } catch {
    /* 신고 실패는 무시 */
  }
}

/** Error 객체·문자열·알 수 없는 값을 신고 가능한 형태로 정리 */
export function toReport(
  scope: string,
  error: unknown,
  extra?: Partial<ClientErrorReport>
): ClientErrorReport {
  if (error instanceof Error) {
    return {
      scope,
      message: error.message || error.name || 'Unknown Error',
      stack: error.stack,
      code: (error as { code?: string }).code,
      digest: (error as { digest?: string }).digest,
      ...extra
    };
  }
  return { scope, message: `${String(error)}`.slice(0, 500), ...extra };
}
